import { EditableSectionContainer, EditableSectionRow, EditableSectionRowTip } from '@uplift/ui-v2';
import { MatchingProfileRow } from '@uplift/types';
import { OptionalTypography } from '../../settings-page/components';
import { useProfileContext } from '../profile.context';
import { useProfileStyles } from '../styles';
import { EthnicityForm, FaithForm, GenderForm, NameForm, PronounsForm, SpokenLanguagesForm, useProfileFormikConfigs } from '../forms';
import { profileRowKeyToLabel } from '../profile.utils';
import { Typography } from '@mui/material';
import { useMemo } from 'react';

export const AboutYouSection: React.FC = () => {
  const { classes } = useProfileStyles();
  const { profile } = useProfileContext();
  const { getFormikConfig } = useProfileFormikConfigs();

  const formikConfigs = useMemo(() => ({
    [MatchingProfileRow.preferredName]: getFormikConfig(MatchingProfileRow.preferredName),
    [MatchingProfileRow.pronouns]: getFormikConfig(MatchingProfileRow.pronouns),
    [MatchingProfileRow.gender]: getFormikConfig(MatchingProfileRow.gender),
    [MatchingProfileRow.faith]: getFormikConfig(MatchingProfileRow.faith),
    [MatchingProfileRow.ethnicity]: getFormikConfig(MatchingProfileRow.ethnicity),
    [MatchingProfileRow.languages]: getFormikConfig(MatchingProfileRow.languages),
  }), [ getFormikConfig ]);

  return (
    <EditableSectionContainer
      title="About You"
      rowDividers
      formikConfigs={formikConfigs}
    >
      <EditableSectionRow
        label={profileRowKeyToLabel.preferredName}
        rowKey={MatchingProfileRow.preferredName}
        form={<NameForm />}
        kind="editable-row"
        editTip={(
          <EditableSectionRowTip
            title="What's this used for?"
            description="We’ll show this name to your clients during matching. If your legal name differs from the name you’d prefer clients to use, update it here."
          />
        )}
      >
        <Typography className={classes.fontWeight500} component={OptionalTypography}>
          {profile.profile?.preferredName}
        </Typography>
      </EditableSectionRow>
      <EditableSectionRow
        label={profileRowKeyToLabel.pronouns}
        rowKey={MatchingProfileRow.pronouns}
        form={<PronounsForm />}
        kind="editable-row"
        editTip={(
          <EditableSectionRowTip
            title="What’s this used for?"
            description="Pronouns inform clients about how to refer to you, rather than relying on assumptions.<br /><br /> You can choose whether or not to display the pronouns you use, based on your own comfort sharing."
          />
        )}
      >
        <Typography className={classes.fontWeight500} component={OptionalTypography}>
          {profile.profile?.pronouns}
        </Typography>
      </EditableSectionRow>
      <EditableSectionRow
        label={profileRowKeyToLabel.gender}
        rowKey={MatchingProfileRow.gender}
        form={<GenderForm />}
        kind="editable-row"
        editTip={(
          <EditableSectionRowTip
            title="What’s this used for?"
            description='During matching, some clients feel more comfortable doing therapy with providers of certain genders. Displaying your gender informs clients about your sense of self, rather than relying on assumptions.<br /><br /> This field asks for your “gender,” not your biological “sex.” Though the terms are often used interchangeably and are connected, gender and sex aren’t the same. To learn more, we recommend Reimagine Gender’s <a href="https://www.reimaginegender.org/understanding-gender" target="_blank">resource about Understanding Gender.</a>'
          />
        )}
      >
        <Typography className={classes.fontWeight500} component={OptionalTypography}>
          {profile.genders.map(e => e.name).join(', ')}
        </Typography>
      </EditableSectionRow>
      <EditableSectionRow
        label={profileRowKeyToLabel.faith}
        rowKey={MatchingProfileRow.faith}
        form={<FaithForm />}
        kind="editable-row"
        editTip={(
          <EditableSectionRowTip
            title="Why are we asking this?"
            description="You can use this field to display your faith. Clients sometimes seek providers who understand or share their faith background."
          />
        )}
      >
        <Typography className={classes.fontWeight500} component={OptionalTypography}>
          {profile.faiths.map(e => e.name).join(', ')}
        </Typography>
      </EditableSectionRow>
      <EditableSectionRow
        label={profileRowKeyToLabel.ethnicity}
        rowKey={MatchingProfileRow.ethnicity}
        form={<EthnicityForm />}
        kind="editable-row"
        editTip={(
          <EditableSectionRowTip
            title="Why are we asking this?"
            description="You can use this to display your race and ethnicity. Clients sometimes seek providers who understand how their culture, race, heritage, or ethnic background shapes their life experience."
          />
        )}
      >
        <Typography className={classes.fontWeight500} component={OptionalTypography}>
          {profile.ethnicities.map(e => e.name).join(', ')}
        </Typography>
      </EditableSectionRow>
      <EditableSectionRow
        label={profileRowKeyToLabel.languages}
        rowKey={MatchingProfileRow.languages}
        form={<SpokenLanguagesForm />}
        kind="editable-row"
        editModalNoPadding
      >
        <Typography className={classes.fontWeight500} component={OptionalTypography}>
          {profile.languages.map(e => e.name).join(', ')}
        </Typography>
      </EditableSectionRow>
    </EditableSectionContainer>
  );
};
